import { usZipcodes } from 'us-zipcodes';
import {
    arrow,
    chevron,
    clock,
    expert,
    hand,
    phone,
    pin,
    home
} from 'icons';
(function () {
    'use strict';

    const STORAGE_KEY = 'bambi-view-in-store-zip';
    const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const DAY_INDEX = Object.fromEntries(DAY_ORDER.map((day, i) => [day.toLowerCase(), i]));
    const EARTH_RADIUS_MILES = 3958.8;
    const DEG_TO_RAD = Math.PI / 180;
    const ACCORDION_SELECTOR = 'wyatt-accordion#accordion-see-feel-it-in-store';

    const BENEFITS_HTML = `
    <ul class="view-in-store-modal__benefits">
      <li class="view-in-store-modal__benefit">${hand}<span>See it. Feel it. Love it.</span></li>
      <li class="view-in-store-modal__benefit-separator" aria-hidden="true"></li>
      <li class="view-in-store-modal__benefit">${home}<span>Take it home today</span></li>
      <li class="view-in-store-modal__benefit-separator" aria-hidden="true"></li>
      <li class="view-in-store-modal__benefit">${expert}<span>Expert help in-store</span></li>
    </ul>
  `;

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

    const formatTime = (timeValue) => {
        const match = String(timeValue || '').match(/^(\d{1,2}):(\d{2})/);
        if (!match) return timeValue || '';

        let hours = Number(match[1]);
        const minutes = match[2];
        const period = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12 || 12;

        return minutes === '00' ? `${hours}${period}` : `${hours}:${minutes}${period}`;
    };

    const getTodayHours = (hours) => {
        if (!Array.isArray(hours) || !hours.length) return null;
        const todayName = DAY_ORDER[(new Date().getDay() + 6) % 7].toLowerCase();
        return hours.find((hour) => String(hour.day).toLowerCase() === todayName) || null;
    };

    const getOpenStatus = (hours) => {
        const today = getTodayHours(hours);
        if (!today?.open || !today?.close) {
            return { statusLabel: 'Closed today', isOpen: false };
        }

        const now = new Date();
        const [openH, openM] = today.open.split(':').map(Number);
        const [closeH, closeM] = today.close.split(':').map(Number);
        const openDate = new Date(now);
        const closeDate = new Date(now);
        openDate.setHours(openH, openM, 0, 0);
        closeDate.setHours(closeH, closeM, 0, 0);

        if (now >= openDate && now < closeDate) {
            return { statusLabel: `Open until ${formatTime(today.close)}`, isOpen: true };
        }

        if (now < openDate) {
            return { statusLabel: `Opens at ${formatTime(today.open)}`, isOpen: false };
        }

        return { statusLabel: 'Closed today', isOpen: false };
    };

    const formatHoursSummary = (hours) => {
        if (!Array.isArray(hours) || !hours.length) return '';

        const sorted = [...hours].sort(
            (a, b) => (DAY_INDEX[String(a.day).toLowerCase()] ?? 99) - (DAY_INDEX[String(b.day).toLowerCase()] ?? 99)
        );
        const groups = [];

        for (const hour of sorted) {
            const timeLabel =
                !hour.open || !hour.close ? 'Closed' : `${formatTime(hour.open)} - ${formatTime(hour.close)}`;
            const last = groups[groups.length - 1];

            if (last && last.timeLabel === timeLabel) {
                last.endDay = hour.day;
            } else {
                groups.push({ startDay: hour.day, endDay: hour.day, timeLabel });
            }
        }

        return groups
            .map((group) => {
                const dayLabel =
                    group.startDay === group.endDay ? group.startDay : `${group.startDay} - ${group.endDay}`;
                return `${dayLabel}: ${group.timeLabel}`;
            })
            .join(' ; ');
    };

    const haversineMiles = (lat1, lon1, lat2, lon2) => {
        const dLat = (lat2 - lat1) * DEG_TO_RAD;
        const dLon = (lon2 - lon1) * DEG_TO_RAD;
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2;

        return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const geocodeZip = (zip) => {
        const place = usZipcodes.find((chunk) => chunk[zip])?.[zip];
        return { zip, latitude: Number(place.latitude), longitude: Number(place.longitude) };
    };

    const normalizeStoreRef = (ref) => {
        if (typeof ref === 'string') {
            return { handle: ref.toLowerCase(), name: ref, location: ref, storeId: '' };
        }

        if (!ref || typeof ref !== 'object') {
            return { handle: '', name: '', location: '', storeId: '' };
        }

        return {
            handle: String(ref.handle || ref.store_handle || '').toLowerCase(),
            name: ref.name || ref.store_name || '',
            location: ref.location || ref.city_state || '',
            storeId: String(ref.store_id || ref.storeId || ref.id || ''),
        };
    };

    const matchLocation = (normalized, location) => {
        if (normalized.name && location.name?.toLowerCase() === normalized.name.toLowerCase()) return true;

        if (normalized.location) {
            const cityState = `${location.city || ''}, ${location.state || ''}`.trim();
            return cityState.toLowerCase() === normalized.location.toLowerCase();
        }

        return false;
    };

    const buildFallbackStore = (normalized) => {
        const [city = normalized.name, state = ''] = String(normalized.location || '')
            .split(',')
            .map((part) => part.trim());

        return {
            handle: normalized.handle || normalized.storeId || normalized.name,
            name: normalized.name || city,
            city,
            state,
            zip: '',
            phone: '',
            address: normalized.location || `${city}${state ? `, ${state}` : ''}`,
            latitude: null,
            longitude: null,
            hours: [],
            onDisplay: true,
        };
    };

    const resolveStores = (storeRefs, locations) => {
        if (!Array.isArray(storeRefs)) return [];

        return storeRefs
            .map((ref) => {
                const normalized = normalizeStoreRef(ref);
                const matched = locations.find((location) => matchLocation(normalized, location));
                return matched ? { ...matched, onDisplay: true } : buildFallbackStore(normalized);
            })
            .filter((store) => store.name || store.city);
    };

    const sortStores = (stores, origin) => {
        if (!origin) return stores;

        return stores
            .map((store) => ({
                ...store,
                distanceMiles:
                    store.latitude != null && store.longitude != null
                        ? haversineMiles(origin.latitude, origin.longitude, store.latitude, store.longitude)
                        : null,
            }))
            .sort((a, b) => {
                if (a.distanceMiles == null && b.distanceMiles == null) return a.name.localeCompare(b.name);
                if (a.distanceMiles == null) return 1;
                if (b.distanceMiles == null) return -1;
                return a.distanceMiles - b.distanceMiles;
            });
    };

    const formatDistance = (miles) => (miles == null ? '' : `${miles.toFixed(1)} mi`);

    const storeLabel = (store) => (store.city && store.state ? `${store.city}, ${store.state}` : store.name);

    const renderVariantSwatch = (variant) => {
        const swatch = variant?.swatch || {};
        const color = swatch.color || '';
        const image = swatch.image || '';
        const isWhite = String(color).toLowerCase() === '#ffffff' || String(variant?.name || '').toLowerCase().includes('white');
        const borderClass = isWhite ? ' variant-swatch--border' : '';

        if (image) {
            return `<img class="variant-swatch${borderClass}" src="${escapeHtml(image)}" alt="" width="20" height="20">`;
        }

        if (color) {
            return `<span class="variant-swatch${borderClass}" style="background:${escapeHtml(color)}"></span>`;
        }

        return `<span class="variant-swatch${borderClass}"></span>`;
    };

    const renderVariantStatus = (onDisplay, takeHome) => {
        let statusHtml = '';

        if (onDisplay && takeHome) {
            statusHtml = `
                <span class="view-in-store-badge badge--on-display">On display</span>
                <span class="view-in-store-badge badge--take-home">Take home</span>
            `;
        } else if (onDisplay) {
            statusHtml = `<span class="view-in-store-badge badge--on-display">On display only</span>`;
        } else if (takeHome) {
            statusHtml = `<span class="view-in-store-badge badge--take-home">Take home only</span>`;
        }

        return `
            <div class="view-in-store-modal__variant-status">
                ${statusHtml}
            </div>
        `;
    };

    const renderModalStore = (store) => {
        if (!store.isActive) return '';

        const status = getOpenStatus(store.hours);
        const variants = store.variants || [];
        const allTakeHomeOnly =
            variants.length > 0 && variants.every((v) => v.takeHome && !v.onDisplay);
        const availabilityLabel = allTakeHomeOnly ? 'Take home only' : 'On display';
        const distance = formatDistance(store.distanceMiles);
        const label = storeLabel(store);
        const mapsLabel = `Bambi Baby Store — ${escapeHtml(store.address)}`;
        const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsLabel || label)}`;
        const locationUrl = store.handle ? `/pages/locations/${store.handle}` : '';
        // console.log('store:', store);
        const variantsHtml = (store.variants && store.variants.length > 0) ? `
      <div class="view-in-store-modal__variants">
      ${store.variants.some(v => v.name !== 'Default Title') ? `
            <p>Variants Available in store:</p>
            ` : ''}
        <ul class="view-in-store-modal__variant-list">
          ${store.variants.map(v => `
          <li class="view-in-store-modal__variant">
          <div class="view-in-store-modal__variant-inner">
          ${v.name !== 'Default Title' ? `
            <div class="view-in-store-modal__variant-swatch">
              ${renderVariantSwatch(v)}
              <strong>${escapeHtml(v.name)}</strong>
            </div>
            ` : ''}
            ${renderVariantStatus(v.onDisplay, v.takeHome)}
            </div>
          </li>
          `).join('')}
        </ul>
      </div>
    ` : '';

        return `
      <details class="view-in-store-modal__item">
        <summary class="view-in-store-modal__summary">
          <span class="view-in-store-modal__summary-main">
            <span class="view-in-store-modal__summary-title">${escapeHtml(label)}${distance ? ` <span class="view-in-store__status-dot--separator" aria-hidden="true"></span> ${escapeHtml(distance)}` : ''}</span>
            <span class="view-in-store__status view-in-store__status--modal">
              <span class="view-in-store__status-dot" aria-hidden="true"></span>
              ${escapeHtml(availabilityLabel)} · ${escapeHtml(status.statusLabel)}
            </span>
          </span>
          ${chevron}
        </summary>
        <div class="view-in-store-modal__details">
        <div class="view-in-store-modal__details-inner">
            ${variantsHtml}
            <p class="view-in-store-modal__address">
              ${pin}
              <a href="${locationUrl}" target="_self" rel="noopener">${escapeHtml(store.address)}</a>
            </p>
            <p class="view-in-store-modal__hours-detail">
              ${clock}
              <span>${escapeHtml(formatHoursSummary(store.hours))}</span>
            </p>
            <div class="view-in-store-modal__actions">
              <a href="${directionsUrl}" target="_blank" rel="noopener" class="view-in-store-modal__action view-in-store-modal__action--primary">
                ${arrow}
                Get Directions
              </a>
              ${store.phone
                ? `<a href="tel:${escapeHtml(store.phone)}" class="view-in-store-modal__action view-in-store-modal__action--secondary">
                      ${phone}
                      Call Store
                    </a>`
                : ''
            }
            </div>
            ${BENEFITS_HTML}
          </div>
        </div>
      </details>
    `;
    };

    class ViewInStore {
        constructor(root) {
            this.root = root;
            this.data = this.parseData();
            this.renderedOrigin = undefined;
            this.zipFormBound = false;
            this.accordionBound = false;
            this.storesRendered = false;
            this._cachedStores = null;
            this.state = { zip: '', origin: null };
            this.cacheDomRefs();
        }

        parseData() {
            const el = document.getElementById('view-in-store-data');
            if (!el) return null;

            try {
                return JSON.parse(el.textContent);
            } catch (error) {
                console.error('View in Store: invalid data JSON', error);
                return null;
            }
        }

        cacheDomRefs() {
            if (!this.modalList) {
                this.modalList = document.getElementById('view-in-store-modal-list');
                if (this.modalList) this.bindAccordion();
            }
            if (!this.sortNote) this.sortNote = document.getElementById('view-in-store-sort-note');
            if (!this.accordion) this.accordion = document.querySelector(ACCORDION_SELECTOR);
            if (!this.zipForm) {
                this.zipForm = document.getElementById('view-in-store-zip-form');
                this.zipInput = this.zipForm?.querySelector('.view-in-store-modal__zip-input') || null;
                this.zipError = this.accordion?.querySelector('.view-in-store-modal__zip-error') || null;
                this.bindZipForm();
            }
        }

        getAllStores() {
            if (this._cachedStores) return this._cachedStores;

            const storesMap = new Map();
            const locations = this.data?.locations || [];

            locations.forEach(store => {
                if (!store.isActive) return;
                const key = store.id || store.handle || store.name;
                storesMap.set(key, { ...store, variants: [] });
            });

            (this.data?.variants || []).forEach(variant => {
                const allRefs = [
                    ...(variant.availableInStores || []),
                    ...(variant.takeHomeAvailableLocation || [])
                ];
                const resolved = resolveStores(allRefs, locations);
                resolved.forEach(store => {
                    if (store.isActive === false) return;
                    const key = store.id || store.handle || store.name;
                    if (!storesMap.has(key)) {
                        storesMap.set(key, { ...store, variants: [] });
                    }
                });
            });

            (this.data?.variants || []).forEach(variant => {
                const variantName = (variant.options || []).join(' ');

                storesMap.forEach((store, key) => {
                    const onDisplay = (variant.availableInStores || []).some((ref) =>
                        matchLocation(normalizeStoreRef(ref), store)
                    );

                    const inventoryRef = (variant.takeHomeAvailableLocation || []).find((ref) =>
                        matchLocation(normalizeStoreRef(ref), store)
                    );
                    const takeHome = inventoryRef && Number(inventoryRef.available) > 0 ? true : false;
                    if (onDisplay || takeHome) {
                        store.variants.push({
                            id: variant.id,
                            name: variantName,
                            status: 'available',
                            takeHome: takeHome,
                            onDisplay: onDisplay,
                            swatch: variant.swatch || null,
                        });
                    }
                });
            });

            const allStores = Array.from(storesMap.values());
            // this._cachedStores = allStores.filter(store => 
            //     store.variants.some(v => v.onDisplay || v.takeHome)
            // );
            this._cachedStores = allStores.filter(store => store.variants.length > 0);
            return this._cachedStores;
        }

        setVisibility(hasStores) {
            this.root.hidden = !hasStores;
            if (this.accordion) this.accordion.hidden = !hasStores;
        }

        updateSortNote() {
            if (!this.sortNote) return;

            if (!this.state.zip) {
                this.sortNote.hidden = true;
                this.sortNote.textContent = '';
                return;
            }

            this.sortNote.hidden = false;
            this.sortNote.textContent = `Stores sorted by distance to ${this.state.zip}`;
        }

        invalidateRenderCache() {
            this.renderedOrigin = undefined;
            this.storesRendered = false;
        }

        render() {
            this.cacheDomRefs();

            const rawStores = this.getAllStores();
            // console.log('rawStores:', rawStores);
            const stores = sortStores(rawStores, this.state.origin);
            const hasStores = stores.length > 0;

            this.setVisibility(hasStores);
            if (this.state.origin === this.renderedOrigin && this.storesRendered) return;

            this.renderedOrigin = this.state.origin;
            this.storesRendered = true;

            if (!this.modalList) return;

            if (!hasStores) {
                this.modalList.innerHTML = '';
                return;
            }

            this.modalList.innerHTML = stores.map(renderModalStore).join('');
            this.updateSortNote();
        }

        async applyZip(zip) {
            const normalizedZip = String(zip || '')
                .trim()
                .replace(/\D/g, '')
                .slice(0, 5);

            if (normalizedZip.length !== 5) {
                this.state.zip = '';
                this.state.origin = null;
                localStorage.removeItem(STORAGE_KEY);
                this.invalidateRenderCache();
                this.render();
                return;
            }

            try {
                this.state.origin = geocodeZip(normalizedZip);
                this.state.zip = normalizedZip;
                localStorage.setItem(STORAGE_KEY, normalizedZip);
                if (this.zipInput) this.zipInput.value = normalizedZip;
            } catch {
                this.state.origin = null;
                this.state.zip = normalizedZip;
            }

            this.invalidateRenderCache();
            this.render();
        }

        bindZipForm() {
            if (!this.zipForm || this.zipFormBound) return;
            this.zipFormBound = true;
            this.zipForm.addEventListener('submit', (event) => {
                event.preventDefault();
                this.applyZip(this.zipInput?.value || '');
            });
        }

        closeAccordionItem(details) {
            if (!details?.open || details.classList.contains('is-closing')) return;

            const panel = details.querySelector('.view-in-store-modal__details');
            if (!panel) {
                details.open = false;
                return;
            }

            details.classList.add('is-closing');

            const onTransitionEnd = (event) => {
                if (event.target !== panel || event.propertyName !== 'grid-template-rows') return;
                panel.removeEventListener('transitionend', onTransitionEnd);
                details.classList.remove('is-closing');
                details.open = false;
            };

            panel.addEventListener('transitionend', onTransitionEnd);
        }

        bindAccordion() {
            if (!this.modalList || this.accordionBound) return;
            this.accordionBound = true;

            this.modalList.addEventListener('click', (event) => {
                const summary = event.target.closest('.view-in-store-modal__summary');
                if (!summary) return;

                const details = summary.closest('.view-in-store-modal__item');
                if (!details) return;

                event.preventDefault();

                if (details.open && !details.classList.contains('is-closing')) {
                    this.closeAccordionItem(details);
                    return;
                }

                if (!details.open) {
                    this.modalList.querySelectorAll('.view-in-store-modal__item[open]').forEach((item) => {
                        if (item !== details) this.closeAccordionItem(item);
                    });
                    details.classList.remove('is-closing');
                    details.open = true;
                }
            });
        }

        bindScrollButton() {
            const button = document.querySelector('.view-in-store-button');
            if (!button) return;

            button.addEventListener('click', (event) => {
                event.preventDefault();
                const accordion =
                    this.accordion || document.querySelector(`.product-details-content ${ACCORDION_SELECTOR}`);
                accordion?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            });
        }

        async init() {
            if (!this.root || !this.data) return;

            this.cacheDomRefs();
            this.state.zip = localStorage.getItem(STORAGE_KEY) || '';
            if (this.zipInput && this.state.zip) this.zipInput.value = this.state.zip;

            this.bindScrollButton();
            this.bindAccordion();

            try {
                if (this.state.zip) await this.applyZip(this.state.zip);
                else this.render();
            } catch {
                this.render();
            }
        }
    }

    const boot = () => {
        const root = document.querySelector('[data-view-in-store]');
        if (root) new ViewInStore(root).init();
    };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();